import { Badge, Button } from '@aaas/shared';
import { X, Plus, Check, Loader2 } from 'lucide-react';
import type { Widget } from '../../types/widget.types';
import WidgetPreviewMini from './WidgetPreviewMini';
import type { KMSWidgetConfig } from '../../widget/types';

interface AllWidgetsCardStackProps {
  widgets: Widget[];
  currentIndex: number;
  isCurrentAssociated: boolean;
  onAdd: () => void;
  onSkip: () => void;
  onReviewAgain: () => void;
  isLoading?: boolean;
}

// Helper function to convert Widget to KMSWidgetConfig
const widgetToConfig = (widget: Widget): Partial<KMSWidgetConfig> => ({
  themeColor: widget.theme_color,
  accentColor: widget.accent_color,
  textColor: widget.text_color,
  buttonColor: widget.button_color,
  buttonIconColor: widget.button_icon_color,
  buttonIcon: widget.button_icon,
  buttonText: widget.button_text,
  buttonBorderRadius: widget.button_border_radius,
  position: widget.position as KMSWidgetConfig['position'],
  widgetSize: widget.widget_size,
  widgetHeight: widget.widget_height,
  popupBorderRadius: widget.popup_border_radius,
  headerTitle: widget.header_title,
  botName: widget.bot_name,
  avatarIcon: widget.avatar_icon,
  showPoweredBy: widget.show_powered_by,
  welcomeMessage: widget.welcome_message,
  inputPlaceholder: widget.input_placeholder,
  sendButtonText: widget.send_button_text,
  suggestedQuestions: widget.suggested_questions || [],
  autoOpen: widget.auto_open,
  autoOpenDelay: widget.auto_open_delay,
  persistConversation: widget.persist_conversation,
  maxMessageLength: widget.max_message_length,
  timeout: widget.timeout,
});

export const AllWidgetsCardStack = ({
  widgets,
  currentIndex,
  isCurrentAssociated,
  onAdd,
  onSkip,
  onReviewAgain,
  isLoading = false,
}: AllWidgetsCardStackProps) => {
  const currentWidget = widgets[currentIndex];

  // All widgets reviewed state
  if (currentIndex >= widgets.length) {
    return (
      <div className="flex items-center justify-center h-[700px]">
        <div className="text-center space-y-6 max-w-md">
          <div className="text-6xl">🎉</div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Done!</h2>
            <p className="text-gray-600">
              You've reviewed all {widgets.length} widget{widgets.length === 1 ? '' : 's'}
            </p>
          </div>
          <button
            onClick={onReviewAgain}
            className="!bg-purple-600 hover:!bg-purple-700 !text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 inline-block mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Review Again
          </button>
        </div>
      </div>
    );
  }

  // No current widget (shouldn't happen, but handle gracefully)
  if (!currentWidget) {
    return (
      <div className="flex items-center justify-center h-[700px]">
        <p className="text-gray-500">No widget to display</p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-2xl" style={{ height: '700px' }}>
      {/* Widget Preview */}
      <div className="relative w-full h-full rounded-2xl shadow-2xl border-0 border-gray-200 overflow-hidden">
        <WidgetPreviewMini config={widgetToConfig(currentWidget)} autoOpen={true} />

        {/* Widget Name - Top Center */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10 mt-2">
          <h3 className="font-semibold text-gray-900 text-base text-center">
            {currentWidget.name}
          </h3>
          {currentWidget.description && (
            <p className="text-xs text-gray-600 mt-1 text-center line-clamp-1 max-w-md">
              {currentWidget.description}
            </p>
          )}
        </div>

        {/* Association Badge - Top Right */}
        {isCurrentAssociated && (
          <div className="absolute top-0 right-4 z-10 mt-2">
            <Badge className="bg-green-500 text-white px-3 py-1 text-sm shadow-md">
              ✓ Associated
            </Badge>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-200">
          <span className="text-sm text-gray-700 font-medium">
            {currentIndex + 1} / {widgets.length}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="absolute -bottom-24 left-1/2 transform -translate-x-1/2 flex items-center gap-6 mt-4">
        {/* Skip Button */}
        <Button
          onClick={onSkip}
          disabled={isLoading}
          variant="outline"
          size="lg"
          className="rounded-full px-8 py-6 text-base font-semibold shadow-md hover:shadow-lg"
          title="Skip (←)"
        >
          <X className="w-5 h-5" />
          Skip
        </Button>

        {/* Add Button */}
        {isCurrentAssociated ? (
          <Button
            disabled
            variant="outline"
            size="lg"
            className="rounded-full px-8 py-6 text-base font-bold border-2 border-green-500 text-green-700 bg-white shadow-lg"
            title="Already added"
          >
            <Check className="w-5 h-5" />
            Added
          </Button>
        ) : (
          <Button
            onClick={onAdd}
            disabled={isLoading}
            size="lg"
            className="rounded-full px-8 py-6 text-base font-bold !bg-emerald-600 hover:!bg-emerald-700 !text-white shadow-lg hover:shadow-xl transition-all"
            title="Add to space (→)"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
            Add
          </Button>
        )}
      </div>
    </div>
  );
};
